// Sugalarm — /api/…/avatar wrappers (cycle 105).
//
// Per-member avatar photos. Upload is multipart/form-data (bypasses the
// JSON `request` helper, same pattern as backgrounds/photo). The raw
// bytes are served behind auth, so a plain <img src> / CSS url() can't
// load them (no Authorization header); `fetchImageObjectUrl` fetches the
// blob with the bearer and hands back an object URL the caller revokes.

import { authedFetch, request } from "../lib/apiClient";
import type { ApiError, ApiErrorKind, ApiResult } from "../lib/types";

export interface AvatarMeta {
  id: string;
  member_id: string;
  content_type: string;
  byte_size: number;
  width: number | null;
  height: number | null;
  created_at: string;
  image_url: string;
}

function classify(status: number): ApiErrorKind {
  if (status === 401) return "auth";
  if (status === 403) return "forbidden";
  if (status === 404) return "not_found";
  if (status === 413) return "validation";
  if (status === 415 || status === 422) return "validation";
  if (status >= 500) return "server";
  return "unknown";
}

async function readError(res: Response): Promise<ApiError> {
  let detail: unknown;
  let message = res.statusText || `HTTP ${res.status}`;
  try {
    const data = await res.json();
    detail = data;
    const d = (data as { detail?: unknown })?.detail;
    if (typeof d === "string") message = d;
  } catch {
    /* not JSON */
  }
  return { kind: classify(res.status), status: res.status, message, details: detail };
}

/** The relative URL of an avatar's image bytes (fetched with auth). */
export function imageUrl(avatarId: string): string {
  return `/api/avatars/${avatarId}/image`;
}

/**
 * Upload (or replace) a member's avatar. The caller always passes the
 * client-cropped, canvas-re-encoded square JPEG `Blob` (never original
 * file bytes) plus its pixel dimensions — so the server only ever stores
 * JPEG, regardless of the source format (HEIC, PNG, …).
 */
export async function upload(
  memberId: string,
  blob: Blob,
  width: number,
  height: number,
  filename?: string,
): Promise<ApiResult<AvatarMeta>> {
  const form = new FormData();
  form.append("image", blob, filename || "avatar.jpg");
  form.append("width", String(width));
  form.append("height", String(height));
  let res: Response;
  try {
    res = await authedFetch(`/api/members/${memberId}/avatar`, {
      method: "POST",
      body: form,
    });
  } catch (e) {
    return {
      ok: false,
      error: {
        kind: "network",
        status: 0,
        message: e instanceof Error ? e.message : "network error",
      },
    };
  }
  if (!res.ok) return { ok: false, error: await readError(res) };
  try {
    return { ok: true, data: (await res.json()) as AvatarMeta };
  } catch {
    return {
      ok: false,
      error: { kind: "unknown", status: res.status, message: "bad response" },
    };
  }
}

export async function remove(memberId: string): Promise<ApiResult<void>> {
  return request<void>(`/api/members/${memberId}/avatar`, { method: "DELETE" });
}

/** Fetch the raw avatar with auth → object URL. Returns null on any
 *  failure (incl. 404 → render the shield). The caller MUST
 *  `URL.revokeObjectURL` it when done / when the id changes. */
export async function fetchImageObjectUrl(
  avatarId: string,
): Promise<string | null> {
  try {
    const res = await authedFetch(imageUrl(avatarId));
    if (!res.ok) return null;
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}
